import { ReactElement, useEffect, useState } from "react";
import { Badge } from "@twilio-paste/core/badge";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Label } from "@twilio-paste/core/label";
import { RadioGroup, Radio } from "@twilio-paste/core/radio-group";
import { SuccessIcon } from "@twilio-paste/icons/esm/SuccessIcon";
import { Text } from "@twilio-paste/core/text";
import { TextArea } from "@twilio-paste/core/textarea";
import { Toaster, useToaster } from "@twilio-paste/core/toast";
import { Stack } from "@twilio-paste/core/stack";
import {
  EmptyStateVariant,
  SurveyResponse,
  WiseOwlEventEnum,
  trackingDetails,
  MessageBlockRole,
  WiseOwlElementIds,
  SurveyVariantEnum,
  QuestionTypeEnum,
  SurveyQuestion,
} from "@twiliointernal/e2-commons-shared-definitions";
import { HelpCenterApi } from "@twiliointernal/e2-commons-shared-api";
import {
  AvatarWrapper,
  EmptyState,
} from "@twiliointernal/e2-commons-shared-ui";
import { noErrorState } from "../../definitions/constants/surveyContext";
import { SurveyErrorVariantsEnum } from "../../definitions/types";
import { useSurveyContext } from "../../hooks";

import { copy } from "./definitions";
import CsatSurveySkeletonLoader from "./sub/CsatSurveySkeletonLoader";

const CsatSurvey = (): ReactElement => {
  const toaster = useToaster();

  const {
    state: {
      chatId,
      displayName,
      errorState: { type: errorType },
      ingress,
      liveChatId,
      submitFeedbackFailed,
      surveyId,
      surveyVariant,
      ticketId,
      token,
      user,
      isSurveySubmitted,
    },
    handlers: {
      handleCancelSurvey,
      setSurveySubmitted: onSuccess,
      setSurveyNotSubmitted,
      setErrorState: setHasError,
      onError,
      onEvent,
      onTrackEvent,
    },
  } = useSurveyContext();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestion[]>([]);
  const [sessionId, setSessionId] = useState<string>("");
  const [responses, setResponses] = useState<SurveyResponse>({});

  const helpCenterApi = new HelpCenterApi({ ingress, token, user });

  useEffect(() => {
    fetchSurveyQuestions();
  }, []);

  // The submit button should be disabled until all multiple choice questions have been answered
  useEffect(() => {
    if (
      !!Object.keys(responses).length &&
      multipleChoiceQuestions.every((question) => {
        return Object.keys(responses).includes(question);
      })
    ) {
      setIsDisabled(false);
    }
  }, [responses]);

  useEffect(() => {
    if (submitFeedbackFailed) {
      toaster.push({
        message: copy.errors.toast,
        variant: "error",
        dismissAfter: 5000,
      });
    }
  }, [submitFeedbackFailed]);

  const fetchSurveyQuestions = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const response = await helpCenterApi.createSurveySession({
        surveyId,
        surveyVariant,
      });
      setSurveyQuestions(response.data.result.questions);
      setSessionId(response.data.result.sessionId);
      setHasError(noErrorState);
    } catch (err) {
      setHasError({
        error: true,
        type: SurveyErrorVariantsEnum.fetchSurveyQuestions,
      });

      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getCsatSurveyQuestions,
          data: { chatId },
          error: err as Error,
        });
      }
    }

    setIsLoading(false);
  };

  const multipleChoiceQuestions =
    surveyQuestions &&
    surveyQuestions
      .filter((question) => question.type === QuestionTypeEnum.MultipleChoice)
      .map((question) => question.questionId);

  const handleSubmitFeedback = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const response = await helpCenterApi.updateSurveySession({
        embeddedData: {
          chatId: chatId,
          chatTimestamp: new Date().toISOString(),
          ingress,
          firstName: user?.firstName,
          lastName: user?.lastName,
          surveyVariant: surveyVariant,
          liveChatId,
          sendGridUserId: user?.sendGridUserId,
          ticketId,
        },
        sessionId,
        surveyId,
        responses,
      });

      if (!response.success) {
        throw new Error("Feedback submission request failed");
      }

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.csatSurveySubmitted,
          data: { chatId },
        });
      }

      if (onTrackEvent) {
        onTrackEvent({
          eventName: trackingDetails.csat.submitSurvey(displayName),
          additionalData: {
            chatId,
            liveChatId,
            ingress,
            ticketId,
            surveyId,
            surveyVariant,
          },
        });
      }

      onSuccess();
    } catch (err) {
      setHasError({
        error: true,
        type: SurveyErrorVariantsEnum.submitSurvey,
      });

      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.csatSurveySubmitted,
          data: { chatId },
          error: err as Error,
        });
      }

      setSurveyNotSubmitted();
    }

    setIsSubmitting(false);
  };

  const renderSurveyQuestions = () => {
    return surveyQuestions.map((question, index) => {
      if (question.type === QuestionTypeEnum.MultipleChoice) {
        return (
          <Box
            key={`box-${index}-${question.questionId}-${question.display}`}
            as="section"
            marginBottom="space60"
          >
            <RadioGroup
              key={`${index}-${question.questionId}-${question.display}`}
              legend={question.display}
              name={question.questionId}
              required
              onChange={(newResponse): void => {
                setResponses({
                  ...responses,
                  [question.questionId]: {
                    [newResponse]: {
                      selected: true,
                    },
                  },
                });
              }}
            >
              {question?.choices &&
                question.choices.map((choice, index) => {
                  const id = `${index}-${question.questionId}-${choice.choiceId}`;

                  return (
                    <Radio
                      key={id}
                      id={id}
                      name={question.questionId}
                      value={choice.choiceId}
                    >
                      {choice.display}
                    </Radio>
                  );
                })}
            </RadioGroup>
          </Box>
        );
      }

      if (question.type === QuestionTypeEnum.TextEntry) {
        return (
          <Box
            key={`box-${index}-${question.questionId}-${question.display}`}
            as="section"
            marginTop="space60"
          >
            <Label htmlFor={question.questionId}>{question.display}</Label>

            <TextArea
              aria-label={question.display}
              id={question.questionId}
              name={question.display}
              onChange={(event) =>
                setResponses({
                  ...responses,
                  [question.questionId]: event.target.value,
                })
              }
            />
          </Box>
        );
      }
    });
  };

  const renderEmptyState = () => {
    return (
      <Box as="main" margin="space60">
        <EmptyState
          heading={copy.errors.emptyState.heading(displayName)}
          description={copy.errors.emptyState.description}
          variant={EmptyStateVariant.oops}
          maxWidth="40rem"
        >
          <Stack orientation="horizontal" spacing="space30">
            <Button
              variant="secondary"
              onClick={handleCancelSurvey}
              disabled={isLoading}
            >
              {copy.formButton.cancel}
            </Button>
            <Button
              variant="primary"
              loading={isLoading}
              onClick={fetchSurveyQuestions}
            >
              {copy.formButton.retry}
            </Button>
          </Stack>
        </EmptyState>
      </Box>
    );
  };

  if (errorType === SurveyErrorVariantsEnum.fetchSurveyQuestions) {
    return renderEmptyState();
  }

  return (
    <>
      {submitFeedbackFailed && (
        <Toaster left={["space40", "unset", "unset"]} {...toaster} />
      )}

      {surveyVariant === SurveyVariantEnum.AGENT && (
        <AvatarWrapper
          element={WiseOwlElementIds.WISE_OWL_CSAT_SURVEY}
          role={MessageBlockRole.User}
          author={`${user?.firstName} ${user?.lastName}`}
          paddingX="space70"
          marginTop="space40"
        >
          <Text as="p">{copy.automatedUserResponse}</Text>
        </AvatarWrapper>
      )}

      <AvatarWrapper
        element={WiseOwlElementIds.WISE_OWL_CSAT_SURVEY}
        role={MessageBlockRole.Assistant}
        paddingX="space70"
        marginTop="space40"
      >
        {/** Feedback Submitted Success Badge */}
        {isSurveySubmitted ? (
          <Box
            display="flex"
            columnGap="space80"
            marginBottom="space60"
            paddingY="space30"
          >
            <Badge as="span" variant="success">
              <SuccessIcon decorative />
              {copy.feedbackSubmitted}
            </Badge>
          </Box>
        ) : (
          /** Survey Form */
          <Box as="section" marginBottom="space60">
            <Box
              as="form"
              borderWidth="borderWidth10"
              borderColor="colorBorderWeaker"
              borderStyle="solid"
              borderRadius="borderRadius20"
              padding="space60"
              onSubmit={handleSubmitFeedback}
            >
              {isLoading && <CsatSurveySkeletonLoader />}

              {surveyQuestions && !isLoading && renderSurveyQuestions()}

              {/** Buttons should not be displayed while questions are loading */}
              {!isLoading && surveyQuestions.length && (
                <Box
                  as="div"
                  display="flex"
                  justifyContent="space-between"
                  marginTop="space60"
                >
                  <Button variant="secondary" onClick={handleCancelSurvey}>
                    {copy.formButton.cancel}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={isDisabled}
                    loading={isSubmitting}
                    onClick={handleSubmitFeedback}
                  >
                    {copy.formButton.submit}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </AvatarWrapper>
    </>
  );
};

export default CsatSurvey;
